import * as XLSX from "xlsx";
import { showToast } from "../../../../components/custom/custom-toast";
import { AttendanceGridRow, ATTENDANCE_GRID_CELL_STATUS } from "../types/attendance";

const parseDateString = (dStr: string): Date => {
  if (dStr.includes("/")) {
    const [d, m, y] = dStr.split("/").map(Number);
    return new Date(y, m - 1, d);
  }
  const [y, m, d] = dStr.split("-").map(Number);
  return new Date(y, m - 1, d);
};

const formatDDMMYYYY = (date: Date): string => {
  const d = String(date.getDate()).padStart(2, "0");
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const y = date.getFullYear();
  return `${d}/${m}/${y}`;
};

const formatDD_MM_YYYY = (date: Date): string => {
  const d = String(date.getDate()).padStart(2, "0");
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const y = date.getFullYear();
  return `${d}-${m}-${y}`;
};

export const exportMonthlyAttendanceExcel = async (
  rows: AttendanceGridRow[],
  fromDate?: string, // DD/MM/YYYY
  toDate?: string, // DD/MM/YYYY
  branchName?: string,
  month?: string // MM/YYYY (dự phòng)
) => {
  if (!rows || rows.length === 0) {
    showToast({
      message: "Không có nhân viên nào có công trong khoảng thời gian để xuất file!",
      type: "warning",
    });
    return;
  }

  let start: Date;
  let end: Date;

  if (fromDate && toDate) {
    start = parseDateString(fromDate);
    end = parseDateString(toDate);
  } else if (month) {
    const [mm, yyyy] = month.split("/").map(Number);
    start = new Date(yyyy, mm - 1, 1);
    end = new Date(yyyy, mm, 0);
  } else {
    const now = new Date();
    start = new Date(now.getFullYear(), now.getMonth(), 1);
    end = now;
  }

  if (start.getTime() > end.getTime()) {
    const tmp = start;
    start = end;
    end = tmp;
  }

  const isSingleMonth = start.getMonth() === end.getMonth() && start.getFullYear() === end.getFullYear();
  const dateList: { dateStr: string; day: number; label: string }[] = [];
  const curr = new Date(start.getFullYear(), start.getMonth(), start.getDate());
  const stop = new Date(end.getFullYear(), end.getMonth(), end.getDate());

  while (curr.getTime() <= stop.getTime()) {
    const day = curr.getDate();
    const mm = String(curr.getMonth() + 1).padStart(2, "0");
    const dd = String(day).padStart(2, "0");
    const yyyy = curr.getFullYear();

    dateList.push({
      dateStr: `${yyyy}-${mm}-${dd}`,
      day,
      label: isSingleMonth ? String(day) : `${dd}/${mm}`,
    });
    curr.setDate(curr.getDate() + 1);
  }

  const totalColumns = 2 + dateList.length + 1; // STT, Họ tên + N ngày + Tổng ngày công

  // Dựng dữ liệu Array of Arrays (aoa)
  const aoa: (string | number)[][] = [];

  // Dòng 0: Tiêu đề chính
  const titleText =
    fromDate && toDate
      ? `BẢNG CHẤM CÔNG (TỪ NGÀY ${formatDDMMYYYY(start)} ĐẾN NGÀY ${formatDDMMYYYY(end)})`
      : `BẢNG CHẤM CÔNG THÁNG ${month || `${String(start.getMonth() + 1).padStart(2, "0")}/${start.getFullYear()}`}`;
  aoa.push([titleText]);

  // Dòng 1: Chi nhánh / Xưởng
  aoa.push([`Chi nhánh / Xưởng: ${branchName || "Tất cả chi nhánh"}`]);

  // Dòng 2: Dòng trống
  aoa.push([]);

  // Dòng 3: Header bảng (1 dòng duy nhất, chỉ có số ngày, không cần thứ)
  const headerRow: (string | number)[] = ["STT", "Họ và tên", ...dateList.map((d) => d.label), "Tổng ngày công"];
  aoa.push(headerRow);

  // Dòng 4 trở đi: Dữ liệu nhân viên có công
  rows.forEach((row, rowIndex) => {
    const name = row.employee?.name || "";

    const dayValues: number[] = dateList.map((d) => {
      const cellData = row.days[d.dateStr] || row.days[String(d.day)];
      if (!cellData) return 0;

      if (cellData.work_day_coefficient !== null && cellData.work_day_coefficient !== undefined) {
        const coeff = Number(cellData.work_day_coefficient);
        return coeff > 0 ? coeff : 0;
      }

      if (
        cellData.status === ATTENDANCE_GRID_CELL_STATUS.ON_TIME ||
        cellData.status === ATTENDANCE_GRID_CELL_STATUS.NO_ATTENDANCE_FULL_DAY
      ) {
        return 1;
      }
      if (
        cellData.status === ATTENDANCE_GRID_CELL_STATUS.LATE_OR_EARLY ||
        cellData.status === ATTENDANCE_GRID_CELL_STATUS.NO_ATTENDANCE_HALF_DAY
      ) {
        return 0.5;
      }
      return 0;
    });

    const totalWorkDays = row.total_work_days !== undefined
      ? row.total_work_days
      : dayValues.reduce((sum, val) => sum + val, 0);

    const dataRow: (string | number)[] = [
      rowIndex + 1,
      name,
      ...dayValues,
      totalWorkDays,
    ];
    aoa.push(dataRow);
  });

  const worksheet = XLSX.utils.aoa_to_sheet(aoa);

  // Merges cho tiêu đề
  worksheet["!merges"] = [
    { s: { r: 0, c: 0 }, e: { r: 0, c: totalColumns - 1 } },
    { s: { r: 1, c: 0 }, e: { r: 1, c: totalColumns - 1 } },
  ];

  // Column widths
  worksheet["!cols"] = [
    { wch: 6 },  // STT
    { wch: 28 }, // Họ và tên
    ...dateList.map(() => ({ wch: 5.5 })), // Ngày 1..N
    { wch: 16 }, // Tổng ngày công
  ];

  const workbook = XLSX.utils.book_new();
  const safeSheetName = "Cham_cong";
  XLSX.utils.book_append_sheet(workbook, worksheet, safeSheetName);

  const fileName =
    fromDate && toDate
      ? `Bang_cham_cong_${formatDD_MM_YYYY(start)}_den_${formatDD_MM_YYYY(end)}.xlsx`
      : `Bang_cham_cong_thang_${(month || `${String(start.getMonth() + 1).padStart(2, "0")}_${start.getFullYear()}`).replace("/", "_")}.xlsx`;

  XLSX.writeFile(workbook, fileName);

  showToast({
    message: "Xuất file chấm công thành công!",
    type: "success",
  });
};
